import { createStore } from 'zustand/vanilla';

export type AudioSettings = {
  musicVolume: number;
  soundVolume: number;
};

export type UserSettingsState = {
  audio: AudioSettings;
  setMusicVolume: (volume: number) => void;
  setSoundVolume: (volume: number) => void;
};

export type CreateUserSettingsStoreOptions = {
  audio?: Partial<AudioSettings>;
};

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = Object.freeze({
  musicVolume: 10,
  soundVolume: 10,
});

export function createUserSettingsStore(options?: CreateUserSettingsStoreOptions) {
  const initialAudio = {
    musicVolume: sanitizeVolume(options?.audio?.musicVolume ?? DEFAULT_AUDIO_SETTINGS.musicVolume),
    soundVolume: sanitizeVolume(options?.audio?.soundVolume ?? DEFAULT_AUDIO_SETTINGS.soundVolume),
  };

  return createStore<UserSettingsState>((set) => ({
    audio: initialAudio,

    setMusicVolume: (volume) => {
      set((state) => ({
        audio: {
          ...state.audio,
          musicVolume: sanitizeVolume(volume),
        },
      }));
    },

    setSoundVolume: (volume) => {
      set((state) => ({
        audio: {
          ...state.audio,
          soundVolume: sanitizeVolume(volume),
        },
      }));
    },
  }));
}

export type UserSettingsStoreApi = ReturnType<typeof createUserSettingsStore>;
export type UserSettingsStoreGetState = UserSettingsStoreApi['getState'];
export type UserSettingsStoreSetState = UserSettingsStoreApi['setState'];

export function sanitizeVolume(volume: number): number {
  if (!Number.isFinite(volume) || Number.isNaN(volume)) {
    return DEFAULT_AUDIO_SETTINGS.musicVolume;
  }

  const rounded = Math.round(volume);
  return Math.min(100, Math.max(0, rounded));
}
